import { api } from "@/lib/api";
import { devLog } from "@/lib/log";

const SW_PATH = "/sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
  } catch (err) {
    devLog.warn("sw register failed", err);
    return null;
  }
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: "denied", subscribed: false, server_count: 0 };
  const permission = Notification.permission;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  // Best-effort — the server count reflects ALL devices this user has ever
  // registered from (this browser + phone + tablet). Don't crash the toggle
  // if the request fails — just fall back to 0.
  let serverCount = 0;
  try {
    const { data } = await api.get("/push/status");
    serverCount = Number(data?.count) || 0;
  } catch (err) {
    devLog.debug("push/status count fetch failed — falling back to 0", err);
  }
  return { supported: true, permission, subscribed: !!sub, server_count: serverCount };
}

export async function sweepPush() {
  const { data } = await api.post("/push/sweep");
  return data;
}

export async function subscribePush() {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error("Service worker registration failed");

  if (Notification.permission === "default") {
    const p = await Notification.requestPermission();
    if (p !== "granted") throw new Error("Notification permission denied");
  } else if (Notification.permission !== "granted") {
    throw new Error("Notifications are blocked in this browser — enable them in site settings");
  }

  const { data } = await api.get("/push/vapid-public");
  const publicKey = data.vapid_public_key;
  if (!publicKey) throw new Error("Server has no VAPID key configured");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    expirationTime: json.expirationTime,
  });
  return true;
}

export async function unsubscribePush() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  const json = sub.toJSON();
  try {
    await api.post("/push/unsubscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
    });
  } catch (err) {
    devLog.warn("server unsubscribe failed", err);
  }
  await sub.unsubscribe();
  return true;
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test", {
    title: "BuildIntel",
    body: "Push notifications are working — you'll hear from us when a job crosses budget.",
    url: "/jobs",
  });
  return data;
}
